import { REGEXP_ONLY_DIGITS } from 'input-otp'
import { AlertCircle } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { requestCode, verifyCode } from '@/api/auth-api'
import { Galaxy } from '@/components/galaxy'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp'
import { Label } from '@/components/ui/label'
import type { Auth } from '@/lib/api'
import { isAdmin, setAuth, takeSignOutNotice } from '@/lib/auth'
import { cn, getApiUrl } from '@/lib/utils'

// Signed in, the galaxy falls into a black hole and light opens out of it
// onto the console: the card goes, the galaxy collapses (1.5s), the light
// takes the last 0.7s.
const ENTER_MS = 1800

// The app's phone login, for admin users only. Laid out as web-v2's
// AuthForm: the number stays in view while the code is typed below it.
export function SignInPage({ onSignedIn }: { onSignedIn: () => void }) {
  const [phone, setPhone] = useState('+49')
  const [code, setCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [entering, setEntering] = useState(false)
  // Opens with the reason the last session ended, if it did.
  const [error, setError] = useState<string | null>(takeSignOutNotice)
  const local = import.meta.env.VITE_ENVIRONMENT === 'local'
  const number = phone.replace(/\s/g, '')

  async function run(action: () => Promise<void>) {
    setBusy(true)
    setError(null)
    try {
      await action()
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Something failed')
    } finally {
      setBusy(false)
    }
  }
  function enter(auth: Auth) {
    setAuth(auth)
    if (!window.matchMedia('(prefers-reduced-motion: no-preference)').matches) {
      onSignedIn()
      return
    }
    setEntering(true)
    setTimeout(onSignedIn, ENTER_MS)
  }
  function sendCode(event: FormEvent) {
    event.preventDefault()
    void run(async () => {
      await requestCode(number)
      setCodeSent(true)
    })
  }
  function resendCode() {
    setCode('')
    void run(async () => {
      await requestCode(number)
    })
  }
  // As in web-v2, the fourth digit submits, and a refused code empties the
  // boxes for the next try.
  function signIn(value: string) {
    void run(async () => {
      try {
        const auth = await verifyCode(number, value)
        if (!isAdmin(auth.accessToken)) {
          throw new Error('Only Handly admin users can sign in here.')
        }
        enter(auth)
      } catch (failure) {
        setCode('')
        throw failure
      }
    })
  }
  function signInAsAdmin() {
    void run(async () => {
      const response = await fetch(
        `${getApiUrl()}/auth/dev-token?user=7&client=lab`,
      )
      const json = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(json.error?.message ?? 'The local API did not answer.')
      }
      enter({ accessToken: json.data.accessToken, refreshToken: '' })
    })
  }

  return (
    <main className="dark relative flex min-h-svh flex-col items-center justify-center overflow-hidden bg-black p-6 text-foreground md:p-10">
      <Galaxy collapse={entering} />
      <div
        className={cn(
          'relative flex w-full max-w-sm flex-col gap-6 transition duration-500',
          entering && 'pointer-events-none scale-75 opacity-0 blur-sm',
        )}
      >
        <div className="flex items-center gap-2 self-center font-medium">
          <img
            src="/logo.svg"
            alt=""
            className="size-6 rounded-md ring-1 ring-white/25"
          />
          Handly Lab
        </div>
        <Card className="rounded-2xl border-white/15 bg-white/[0.02] shadow-[0_24px_80px_-24px_rgb(255_114_206/0.35)] backdrop-blur-sm">
          <CardHeader className="text-center">
            <CardTitle className="font-medium text-xl tracking-tight">
              {codeSent ? 'Enter the code' : 'Sign in'}
            </CardTitle>
            <CardDescription>
              {codeSent
                ? `We sent a 4-digit code to ${number}.`
                : 'With your Handly admin phone number'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="grid gap-6" onSubmit={sendCode}>
              <div className="grid gap-3">
                <Label htmlFor="phone" className="text-muted-foreground">
                  Phone number
                </Label>
                <Input
                  id="phone"
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  type="tel"
                  autoComplete="tel"
                  disabled={codeSent || busy}
                  autoFocus
                  className="h-10 autofill:shadow-[inset_0_0_0_100px_#171717] autofill:[-webkit-text-fill-color:#fafafa]"
                />
              </div>
              {codeSent ? (
                <div className="grid gap-3">
                  <Label htmlFor="code" className="text-muted-foreground">
                    Code from the SMS
                  </Label>
                  <div className="flex justify-center">
                    <InputOTP
                      id="code"
                      maxLength={4}
                      pattern={REGEXP_ONLY_DIGITS}
                      value={code}
                      onChange={setCode}
                      onComplete={signIn}
                      disabled={busy || entering}
                      autoFocus
                    >
                      <InputOTPGroup>
                        {[0, 1, 2, 3].map((index) => (
                          <InputOTPSlot
                            key={index}
                            index={index}
                            className="size-12 text-lg"
                          />
                        ))}
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                  <div className="flex justify-center">
                    <Button
                      type="button"
                      variant="link"
                      className="text-muted-foreground hover:text-foreground"
                      onClick={resendCode}
                      disabled={busy}
                    >
                      Resend code
                    </Button>
                    <Button
                      type="button"
                      variant="link"
                      className="text-muted-foreground hover:text-foreground"
                      onClick={() => {
                        setCodeSent(false)
                        setCode('')
                      }}
                      disabled={busy}
                    >
                      Use another number
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  type="submit"
                  className="h-10 w-full rounded-full shadow-[0_0_24px_rgb(255_255_255/0.25)]"
                  disabled={busy || number.length < 6}
                >
                  {busy ? 'Sending…' : 'Send code'}
                </Button>
              )}
              {error ? (
                <p
                  role="alert"
                  className="flex items-center gap-2 rounded-md border border-destructive/20 bg-destructive/10 p-3 text-destructive text-sm"
                >
                  <AlertCircle className="size-4 shrink-0" />
                  {error}
                </p>
              ) : null}
            </form>
          </CardContent>
        </Card>
        {local ? (
          <Button
            variant="ghost"
            className="text-muted-foreground"
            onClick={signInAsAdmin}
          >
            Sign in as Handly Admin
          </Button>
        ) : null}
      </div>
      {entering ? (
        // The light out of the hole: white, as the console behind it.
        <div className="fade-in zoom-in-0 pointer-events-none fixed top-1/2 left-1/2 size-[300vmax] -translate-x-1/2 -translate-y-1/2 animate-in rounded-full bg-white fill-mode-both delay-1100 duration-700 ease-in" />
      ) : null}
    </main>
  )
}
